import React from "react";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";

// React ErrorBoundary class component. Must be a class because React error
// boundaries can't be hooks. Catches any error thrown during render or
// commit inside its children and shows a recovery UI instead of a frozen /
// blank screen.
//
// Used to wrap the Game canvas (R3F + useFrame loops are the most likely
// source of crashes — see the Companion Ships freeze in PRD). If the loop
// throws, the user sees a friendly "Something went wrong" card with two
// recovery options instead of having to refresh the browser tab.
class GameErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Surface to the console — the deployed app's source maps will give us
    // a usable stack trace. In the future we can POST this to a backend
    // /api/client-errors endpoint for centralized logging.
    console.error("[GameErrorBoundary] Caught render error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReturnToMenu = () => {
    // Tell the parent to navigate back to menu and reset run state.
    this.props.onReturnToMenu?.();
    this.setState({ error: null, errorInfo: null });
  };

  handleRestart = () => {
    // Tell the parent to remount the game with a fresh run.
    this.props.onRestart?.();
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error) || "Unknown error";
      return (
        <div className="screen error-screen" data-testid="game-error-boundary">
          <div className="error-card">
            <div className="error-icon">
              <AlertTriangle size={48} />
            </div>
            <h2 className="error-title">Something went wrong</h2>
            <p className="error-msg">
              The game hit a bug and stopped. Your account, coins, and unlocks are safe.
            </p>
            <details className="error-details">
              <summary>Technical details</summary>
              <code>{msg}</code>
            </details>
            <div className="error-buttons">
              <button
                type="button"
                className="zc-btn primary"
                onClick={this.handleReturnToMenu}
                data-testid="error-return-menu"
              >
                <Home size={16} /> Return to Menu
              </button>
              <button
                type="button"
                className="zc-btn ghost"
                onClick={this.handleRestart}
                data-testid="error-restart"
              >
                <RotateCcw size={16} /> Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default GameErrorBoundary;
